import { createSelector } from "@reduxjs/toolkit";
import { IPixPath, IPixPolygon } from "../../components/PixBimViewer/types";
import { convertMcBimSpaceToPixPolygon, convertMcBimItemToPixPolygons } from "../../utils";
import {
  selectDebugMcbimItems,
  selectDebugActiveBuildingStoryName,
  selectDebugHiddenTypeClasses,
  selectDebugItemsToShow,
} from "../DebugSlice";
import { IRootState } from "../../app/store";

export const selectDebugCount = createSelector(selectDebugMcbimItems, (items) => items.length);

export const selectDebugUniqueBuildingStoryNames = createSelector(
  selectDebugMcbimItems,
  (items): string[] => {
    const names = new Set<string>();
    for (const it of items) {
      const name =
        (it as any)?.buildingStory?.name && String((it as any).buildingStory.name).trim() !== ""
          ? String((it as any).buildingStory.name)
          : "(uten etasje)";
      names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "nb"));
  }
);

// Items with geometry on active storey
const selectFreeTextSearch = (s: IRootState) => s.debug.freeTextSearch ?? "";
export const selectDebugMcItemsWithGeomOnActiveBuildingStory = createSelector(
  selectDebugMcbimItems,
  selectDebugActiveBuildingStoryName,
  selectFreeTextSearch,
  (items, activeName, freeTextSearch) => {
    if (!activeName) return [];
    return items.filter((it) => {
      if (it.polygon == null && it.bbox == null) return false;
      if (freeTextSearch !== "") {
        const search = freeTextSearch.toUpperCase();
        const inName = it.name && String(it.name).toUpperCase().includes(search);
        const inLongName = it.longName && String(it.longName).toUpperCase().includes(search);
        if (!inName && !inLongName) {
          return false;
        }
      }
      const name =
        (it as any)?.buildingStory?.name && String((it as any).buildingStory.name).trim() !== ""
          ? String((it as any).buildingStory.name)
          : "(uten etasje)";
      return name === activeName;
    });
  }
);

// Spaces → Pix polygons (Spaces påverkas inte av hide-filtret)
export const selectDebugSpacePixPolygonsFromMcbimItems = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items): IPixPolygon[] => {
    return items
      .filter((it) => it.instanceClass === "IfcSpace")
      .map((it) => convertMcBimSpaceToPixPolygon(it, false))
      .filter(
        (it) =>
          it !== undefined &&
          JSON.stringify(it.text.values).toUpperCase().includes("AREA") === false
      ) as IPixPolygon[];
  }
);

// Non-spaces → Pix polygons (respekterar hide-filtret)
export const selectDebugNonSpacesPixPolygonsFromMcbimItems = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  selectDebugHiddenTypeClasses,
  (items, hiddenTypeClasses): IPixPolygon[] => {
    const base = items.filter((it) => it.instanceClass !== "IfcSpace");
    const hasHidden = Array.isArray(hiddenTypeClasses) && hiddenTypeClasses.length > 0;
    const hidden = new Set((hiddenTypeClasses ?? []).map((s) => s.trim()).filter(Boolean));

    const filtered = hasHidden
      ? base.filter((it) => {
          const tc = (it as any)?.type?.typeClass;
          if (!tc) return true; // saknar typeClass -> döljs inte
          return !hidden.has(String(tc).trim());
        })
      : base;

    return filtered
      .map((it) => convertMcBimItemToPixPolygons(it))
      .filter((p): p is IPixPolygon => p !== undefined);
  }
);

// All (spaces + non-spaces) → Pix polygons
export const selectAllDebugPixPolygonsFromMcbimItems = createSelector(
  selectDebugSpacePixPolygonsFromMcbimItems,
  selectDebugNonSpacesPixPolygonsFromMcbimItems,
  (spaces, nonSpaces) => [...spaces, ...nonSpaces]
);

// FINAL: filter-aware polygons to render based on itemsToShow
export const selectDebugPixPolygonsFiltered = createSelector(
  selectDebugItemsToShow,
  selectDebugSpacePixPolygonsFromMcbimItems,
  selectDebugNonSpacesPixPolygonsFromMcbimItems,
  selectAllDebugPixPolygonsFromMcbimItems,
  (itemsToShow, spaces, nonSpaces, all) => {
    switch (itemsToShow) {
      case "spaces":
        return spaces;
      case "non-spaces":
        return nonSpaces;
      default:
        return all;
    }
  }
);

// Typen du bad om
export type PixBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

// Selector som returnerar PixBounds | null
export const selectViewerPixBounds = createSelector(
  selectDebugPixPolygonsFiltered,
  (polys): PixBounds | null => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const poly of polys) {
      if (!poly?.points?.length) continue;
      for (const pt of poly.points) {
        if (!Array.isArray(pt) || pt.length < 2) continue;
        const x = Number(pt[0]);
        const y = Number(pt[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (!Number.isFinite(minX)) return null;
    return { minX, minY, maxX, maxY };
  }
);

// Unika type.typeClass-värden för aktiva byggnadsplanet (för UI-listor etc)
export const selectDebugUniqueTypeClassesOnActiveBuildingStory = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items): string[] => {
    const classes = new Set<string>();
    for (const it of items) {
      const tc = (it as any)?.type?.typeClass;
      if (tc && typeof tc === "string" && tc.trim() !== "") {
        classes.add(tc.trim());
      }
    }
    return Array.from(classes).sort((a, b) => a.localeCompare(b, "nb"));
  }
);

export const selectIfcSpacesWithMissingPolygon = createSelector(selectDebugMcbimItems, (items) => {
  return items.filter(
    (it) => it.instanceClass === "IfcSpace" && (it.polygon == null || it.polygon.length === 0)
  );
});

export const selectDebugCeilingGridPixPaths = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items): IPixPath[] => {
    const paths: IPixPath[] = [];
    let index = 0;

    for (const item of items) {
      const grid = item.ceilingGrid;
      if (!grid || !Array.isArray(grid.lines) || grid.lines.length === 0) continue;

      for (const line of grid.lines) {
        if (!Array.isArray(line) || line.length !== 2) continue;
        const start = line[0];
        const end = line[1];
        if (!Array.isArray(start) || start.length !== 3 || !Array.isArray(end) || end.length !== 3)
          continue;

        const points: number[][] = [
          [Number(start[0]), Number(start[1])], // Ignoring z for 2D floorplan
          [Number(end[0]), Number(end[1])],
        ];

        paths.push({
          id: `${item._id ?? "item"}-${index++}`,
          points,
          strokeColor: "#ff0000", // Default black; adjust as needed
          strokeWidth: "20", // Default width; adjust as needed
          tooltip: item.name ? `Ceiling line in ${item.name}` : undefined,
        });
      }
    }

    return paths;
  }
);
